import type { ProjectIntent, ProjectPhase, SiteRagReport } from '../../types/siteRag';
import { bucketFromInServiceDate } from '../projectIntent';
import { resolveDevelopmentPathwayPhase } from '../reconcileDevelopmentPathway';
import { isIntentFieldExplicitlyUnknown } from '../intent/explicitUnknown';

/** A period remains a period: comparing both endpoints never invents an exact COD. */
function periodBounds(text: string): [string, string] | null {
  const exact = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (exact) {
    const value = new Date(`${text}T00:00:00Z`);
    return Number.isFinite(value.getTime()) && value.toISOString().slice(0, 10) === text ? [text, text] : null;
  }
  const quarter = /^Q([1-4])\s+(20\d{2})$/i.exec(text);
  const year = /^(20\d{2})$/.exec(text);
  if (!quarter && !year) return null;
  const y = Number(quarter?.[2] ?? year![1]);
  const startMonth = quarter ? (Number(quarter[1]) - 1) * 3 : 0;
  const endMonth = quarter ? startMonth + 3 : 12;
  return [new Date(Date.UTC(y, startMonth, 1)).toISOString(),
    new Date(Date.UTC(y, endMonth, 1) - 1).toISOString()];
}

function recordedPeriod(phase: ProjectPhase, displayed: string): [string, string] | null {
  if (phase.targetInServiceDate) return displayed === phase.targetInServiceDate ? periodBounds(displayed) : null;
  // Notes may contain source dates or several alternative schedules. Require
  // one explicitly targeted period, and agreement with the displayed period.
  const clauses = (phase.notes ?? '').split(/(?<=[.!?])\s+|;\s*|\n+/);
  if (clauses.some(clause => /\b(?:targets?|in-service|COD|buildout)\b/i.test(clause)
    && /\b(?:Q[1-4]\s+20\d{2}|20\d{2})\b/i.test(clause)
    && !affirmativePhaseTarget(clause, phase.sequence))) return null;
  const periods = clauses.flatMap(clause => {
    if (!affirmativePhaseTarget(clause, phase.sequence)) return [];
    return [
      ...[...clause.matchAll(/\b(?:targets?|in-service(?: date)?(?: of)?|COD(?: of)?|buildout by)\s+(?:ultimate buildout by\s+)?(Q[1-4]\s+20\d{2}|20\d{2})\b/gi)].map(match => match[1]!),
      ...[...clause.matchAll(/\b(Q[1-4]\s+20\d{2}|20\d{2})\s+(?:is|was|remains)\s+(?:(?:the|an|existing|project|planning|proposed)\s+){0,5}target\b/gi)].map(match => match[1]!),
    ];
  }).map(period => period.replace(/\s+/g, ' ').toUpperCase());
  const unique = [...new Set(periods)];
  return unique.length === 1 && unique[0] === displayed.toUpperCase() ? periodBounds(displayed) : null;
}

function affirmativePhaseTarget(text: string, sequence: number): boolean {
  return /\b(?:targets?|in-service|COD|buildout)\b/i.test(text)
    && !/\b(?:if|unless|whether|suppose|hypothetical|alternative|alternatively|instead|or|could|might|may)\b/i.test(text)
    && !/\b(?:does? not|never|no longer)\s+targets?\b|\bnot\s+(?:(?:a|the|our|current|project)\s+)*target\b/i.test(text)
    && ![...text.matchAll(/\bphase\s+(\d+)\b/gi)].some(match => Number(match[1]) !== sequence);
}

/** Correct only an explicit arithmetic comparison of a matching owner target
 * against the broad intent window. Uses the frozen report date, never today's
 * reader date, and does not infer a feasible/committed delivery schedule. */
export function reconcilePhaseTimelineClaims(
  report: SiteRagReport,
  intent: ProjectIntent | null | undefined = report.projectIntentAtGen,
): SiteRagReport {
  if (report.reportType !== 'feasibility' || (!Array.isArray(report.developmentPathway) || !report.developmentPathway.length) || !intent
    || isIntentFieldExplicitlyUnknown(intent, 'timeline') || intent.timeline === 'undecided') return report;
  const reference = new Date(report.createdAt);
  if (!Number.isFinite(reference.getTime())) return report;
  const asOf = reference.toISOString().slice(0, 10);
  const expectedLabel = { 'under-2yr': '(?:under|less than|<)\\s*2', '2-5yr': '2\\s*[-–]\\s*5', 'over-5yr': '(?:over|more than|>)\\s*5|5\\s*\\+' }[intent.timeline];
  if (!expectedLabel) return report;
  const comparison = new RegExp(`\\bThis target is (outside|within) (the project's high-level (?:${expectedLabel})[ -]years? timeline objective)`, 'gi');
  const developmentPathway = report.developmentPathway.map(row => {
    // Generated rows can omit narrative fields even after a review pass.
    // Withhold projection on malformed rows without inventing identity/prose;
    // retain the original row for the existing quality/schema diagnostics.
    if (!row || typeof row !== 'object' || typeof row.phase !== 'string'
      || typeof row.title !== 'string' || typeof row.description !== 'string') return row;
    const phase = resolveDevelopmentPathwayPhase(row, intent.phases ?? []);
    const timeline = (row as typeof row & { timeline?: unknown }).timeline;
    const displayed = typeof timeline === 'string' ? timeline.trim()
      : typeof row.cod === 'string' ? row.cod.trim() : undefined;
    if (!phase || !displayed) return row;
    const bounds = recordedPeriod(phase, displayed);
    if (!bounds) return row;
    const buckets = bounds.map(date => bucketFromInServiceDate(date, reference));
    if (buckets.includes('undecided') || buckets[0] !== buckets[1]) return row;
    const relation = buckets[0] === intent.timeline ? 'within' : 'outside';
    const description = row.description.replace(comparison, (text, claimed: string, objective: string, offset: number) => {
      // "This target" must refer to the matching owner period in the immediately
      // preceding sentence, not a different equipment/procurement target.
      const prior = row.description.slice(0, offset).trim().split(/(?<=[.!?])\s+|\n+/).at(-1) ?? '';
      const namedPeriods = [...prior.matchAll(/\b(?:\d{4}-\d{2}-\d{2}|Q[1-4]\s+20\d{2}|20\d{2})\b/gi)]
        .map(match => match[0].replace(/\s+/g, ' ').toUpperCase());
      if (!affirmativePhaseTarget(prior, phase.sequence) || !namedPeriods.length
        || namedPeriods.some(period => period !== displayed.toUpperCase())) return text;
      return claimed.toLowerCase() === relation ? text : `This target is ${relation} ${objective} as of ${asOf}`;
    });
    return description === row.description ? row : { ...row, description };
  });
  return developmentPathway.some((row, i) => row !== report.developmentPathway![i])
    ? { ...report, developmentPathway } : report;
}
